import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiX,
  FiPlus,
  FiCalendar,
  FiUser,
  FiFlag,
  FiTag,
  FiClock,
  FiCheckSquare,
  FiTrash2,
} from "react-icons/fi";
import "./CreateTaskModal.css";

const CreateTaskModal = ({ roomId, members, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    priority: "normal",
    assignedTo: "",
    dueDate: "",
    researchRoomId: roomId,
    startDate: "",
    subtasks: [],
    estimatedHours: "",
    labels: "",
    category: "research",
  });

  const [subtaskInput, setSubtaskInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const addSubtask = () => {
    if (!subtaskInput.trim()) return;
    setFormData({
      ...formData,
      subtasks: [...formData.subtasks, { title: subtaskInput.trim() }],
    });
    setSubtaskInput("");
  };

  const removeSubtask = (index) => {
    setFormData({
      ...formData,
      subtasks: formData.subtasks.filter((_, i) => i !== index),
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = "Title is required";
    if (!formData.description.trim()) newErrors.description = "Description is required";
    if (!formData.assignedTo) newErrors.assignedTo = "Please assign this task";
    if (!formData.dueDate) newErrors.dueDate = "Due date is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const taskData = {
        ...formData,
        labels: formData.labels
          ? formData.labels.split(",").map((l) => l.trim()).filter(Boolean)
          : [],
        estimatedHours: formData.estimatedHours ? parseFloat(formData.estimatedHours) : null,
      };
      if (!taskData.startDate) delete taskData.startDate;
      if (!taskData.estimatedHours) delete taskData.estimatedHours;

      await onSuccess(taskData);
      onClose();
    } catch (error) {
setErrors({ submit: error.message || "Failed to create task" });
    } finally {
      setSubmitting(false);
    }
  };

  const priorityOptions = [
    { value: "low", label: "Low", color: "#10b981" },
    { value: "normal", label: "Normal", color: "#6366f1" },
    { value: "high", label: "High", color: "#f59e0b" },
    { value: "urgent", label: "Urgent", color: "#ef4444" },
  ];

  const categoryOptions = [
    "Research", "Writing", "Analysis", "Experiment", "Review", "Admin", "Other"
  ];

  return (
    <div className="rcm-overlay" onClick={onClose}>
      <motion.div
        className="rcm-modal"
        initial={{ opacity: 0, y: 50, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 50, scale: 0.95 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="rcm-header">
          <h2 className="rcm-title">Create New Task</h2>
          <button className="rcm-close" onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="rcm-form">
          {/* Title */}
          <div className="rcm-field">
            <label className="rcm-label">
              Task Title <span className="rcm-required">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Enter task title..."
              className={`rcm-input ${errors.title ? "rcm-input--error" : ""}`}
            />
            {errors.title && <span className="rcm-error">{errors.title}</span>}
          </div>

          {/* Description */}
          <div className="rcm-field">
            <label className="rcm-label">
              Description <span className="rcm-required">*</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Describe the task..."
              rows="3"
              className={`rcm-textarea ${errors.description ? "rcm-input--error" : ""}`}
            />
            {errors.description && <span className="rcm-error">{errors.description}</span>}
          </div>

          {/* Priority & Category Row */}
          <div className="rcm-row">
            <div className="rcm-field rcm-field--half">
              <label className="rcm-label">
                <FiFlag size={14} /> Priority
              </label>
              <div className="rcm-priority-options">
                {priorityOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`rcm-priority-btn ${formData.priority === opt.value ? "rcm-priority-btn--active" : ""}`}
                    onClick={() => setFormData({ ...formData, priority: opt.value })}
                    style={{ "--priority-color": opt.color }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="rcm-field rcm-field--half">
              <label className="rcm-label">
                <FiTag size={14} /> Category
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="rcm-select"
              >
                {categoryOptions.map((cat) => (
                  <option key={cat.toLowerCase()} value={cat.toLowerCase()}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Assignee */}
          <div className="rcm-field">
            <label className="rcm-label">
              <FiUser size={14} /> Assign To <span className="rcm-required">*</span>
            </label>
            <select
              name="assignedTo"
              value={formData.assignedTo}
              onChange={handleChange}
              className={`rcm-select ${errors.assignedTo ? "rcm-input--error" : ""}`}
            >
              <option value="">Select member...</option>
              {members.map((member) => (
                <option key={member.user._id} value={member.user._id}>
                  {member.user.name} ({member.role})
                </option>
              ))}
            </select>
            {errors.assignedTo && <span className="rcm-error">{errors.assignedTo}</span>}
          </div>

          {/* Date Row */}
          <div className="rcm-row">
            <div className="rcm-field rcm-field--half">
              <label className="rcm-label">
                <FiCalendar size={14} /> Start Date
              </label>
              <input
                type="datetime-local"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                className="rcm-input"
              />
            </div>

            <div className="rcm-field rcm-field--half">
              <label className="rcm-label">
                <FiCalendar size={14} /> Due Date <span className="rcm-required">*</span>
              </label>
              <input
                type="datetime-local"
                name="dueDate"
                value={formData.dueDate}
                onChange={handleChange}
                className={`rcm-input ${errors.dueDate ? "rcm-input--error" : ""}`}
              />
              {errors.dueDate && <span className="rcm-error">{errors.dueDate}</span>}
            </div>
          </div>

          {/* Estimated Hours & Labels */}
          <div className="rcm-row">
            <div className="rcm-field rcm-field--half">
              <label className="rcm-label">
                <FiClock size={14} /> Estimated Hours
              </label>
              <input
                type="number"
                name="estimatedHours"
                value={formData.estimatedHours}
                onChange={handleChange}
                placeholder="e.g., 8"
                min="0"
                step="0.5"
                className="rcm-input"
              />
            </div>

            <div className="rcm-field rcm-field--half">
              <label className="rcm-label">Labels</label>
              <input
                type="text"
                name="labels"
                value={formData.labels}
                onChange={handleChange}
                placeholder="Comma-separated..."
                className="rcm-input"
              />
            </div>
          </div>

          {/* Subtasks */}
          <div className="rcm-field">
            <label className="rcm-label">
              <FiCheckSquare size={14} /> Subtasks
            </label>
            <div className="rcm-subtasks">
              <AnimatePresence>
                {formData.subtasks.map((subtask, index) => (
                  <motion.div
                    key={index}
                    className="rcm-subtask-item"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <span className="rcm-subtask-number">{index + 1}.</span>
                    <span className="rcm-subtask-text">{subtask.title}</span>
                    <button
                      type="button"
                      className="rcm-subtask-remove"
                      onClick={() => removeSubtask(index)}
                    >
                      <FiTrash2 size={14} />
                    </button>
                  </motion.div>
                ))}
              </AnimatePresence>

              <div className="rcm-subtask-add">
                <input
                  type="text"
                  value={subtaskInput}
                  onChange={(e) => setSubtaskInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addSubtask();
                    }
                  }}
                  placeholder="Add a subtask..."
                  className="rcm-input"
                />
                <button
                  type="button"
                  className="rcm-subtask-btn"
                  onClick={addSubtask}
                >
                  <FiPlus size={16} />
                </button>
              </div>
            </div>
          </div>

          {/* Submit Error */}
          {errors.submit && <div className="rcm-submit-error">{errors.submit}</div>}

          {/* Actions */}
          <div className="rcm-actions">
            <button
              type="button"
              className="rcm-btn rcm-btn--secondary"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rcm-btn rcm-btn--primary"
              disabled={submitting}
            >
              {submitting ? "Creating..." : "Create Task"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default CreateTaskModal;
